// GES-15 credit limit — pure DISPLAY helpers. No money arithmetic: they only read the exact 2-dp TEXT the API
// returns (the gate's 4xx `credit` block, GET /api/partners/:id/credit-exposure, the partners row).
import { isNonZeroText } from './discountDisplay.mjs';

// The gate refusal codes -> notice kind (keys under sales:credit.kind.*).
const KIND = {
  CREDIT_LIMIT_EXCEEDED: 'block', CREDIT_LIMIT_WARNING: 'warn',
  CREDIT_CHECK_UNAVAILABLE: 'unavailable', CREDIT_OVERRIDE_AUDIT_FAILED: 'audit_failed',
  // E5 (GES-440): the override resend itself refused (reason missing / not admin / key reused). Shown as a refusal, nothing to resend.
  CREDIT_OVERRIDE_REASON_REQUIRED: 'override_refused', CREDIT_OVERRIDE_ADMIN_ONLY: 'override_refused', CREDIT_OVERRIDE_IDEMPOTENCY_CONFLICT: 'override_refused',
};
export const OVERRIDE_REASON_MIN = 3; export const OVERRIDE_REASON_MAX = 500;
// E5 figures the server states at the top level of the refusal (exposure NOW, what this action adds, after, limit); headroom is NOW.
export const SERVER_FIGURES = ['exposure', 'adds', 'exposure_after', 'limit'];
export const CREDIT_ERROR_CODES = Object.keys(KIND);
// The figures shown on a notice, in order (keys under sales:credit.fig.*).
export const CREDIT_FIGURES = ['current', 'ar', 'committed', 'exposure_after', 'limit'];

// "-21000.00" -> { over: true, amount: "21000.00" }; a text test, never a numeric compare.
export function headroomOf(text) {
  if (text == null) return null;
  const s = String(text);
  return s.startsWith('-') ? { over: true, amount: s.slice(1) } : { over: false, amount: s };
}

// A refusal body (thrown api error `.body`, or requestRaw `.data`) -> the notice model, or null when it is not a
// credit refusal. Proceed (resend with credit_override) only on the admin 409, or the 503 for an admin (D16).
export function creditNotice(body, isAdmin = false, partnerName = '') {
  const code = body && body.code;
  if (!KIND[code]) return null;
  const c = body.credit || null;
  const top = body.exposure != null && body.adds != null;   // E5 body; an older server falls back to the credit{} block
  return {
    code, kind: KIND[code], message: body.error || '', messageFr: body.error_fr || '',
    partner: (c && c.partner_name) || partnerName || '', state: c ? c.state : null,   // 503 has no credit block: the surface's name
    // cash only: the limit row is kept even when the API sends limit null (shown as "None (cash only)")
    figures: top ? SERVER_FIGURES.filter((k) => body[k] != null || (k === 'limit' && c && c.state === 'cash_only')).map((k) => [k, body[k]])
      : c ? CREDIT_FIGURES.filter((k) => c[k] != null || (k === 'limit' && c.state === 'cash_only')).map((k) => [k, c[k]]) : [],
    headroom: headroomOf(top ? body.headroom : c ? c.headroom : null),
    canOverride: code === 'CREDIT_LIMIT_WARNING' || (code === 'CREDIT_CHECK_UNAVAILABLE' && isAdmin === true),
    // a non-admin 422 still shows an Override button, greyed ("Admin only"); the 503 / audit / override refusals have none
    showOverride: code === 'CREDIT_LIMIT_WARNING' || code === 'CREDIT_LIMIT_EXCEEDED' || code === 'CREDIT_CHECK_UNAVAILABLE',
    // owner rule: a legacy limit on a SHARED account was treated as cash only; the API names the account (reason_code + shared_account)
    sharedAccount: body.reason_code === SHARED_CASH_ONLY && body.shared_account ? body.shared_account : null,
  };
}

// A reason that may be sent as credit_override_reason (3..500 characters once trimmed).
export const overrideReasonOk = (r) => { const n = String(r || '').trim().length; return n >= OVERRIDE_REASON_MIN && n <= OVERRIDE_REASON_MAX; };

// Owner rule (no credit on a shared receivable account). The partners row / credit-exposure carry the explanation object
// ({ account_name, reason, legacy_credit, … }) or null; a partner write is refused with 422 CREDIT_LIMIT_SHARED_ACCOUNT, or 409
// CREDIT_LIMIT_SHARED_ACCOUNT_WOULD_RESET { account, affected[], can_confirm } until an admin repeats it with confirm_reset_credit.
export const SHARED_CASH_ONLY = 'CREDIT_SHARED_ACCOUNT_CASH_ONLY';
export const WOULD_RESET = 'CREDIT_LIMIT_SHARED_ACCOUNT_WOULD_RESET';
export const sharedAccountOf = (row) => (row && (row.credit_shared_account || row.shared_account)) || null;
// i18n key + params of the shared-account line: names the account only when it differs from the customer.
export const sharedReasonArgs = (customer, account) => (!customer || !account || String(customer).trim().toLowerCase() === String(account).trim().toLowerCase())
  ? ['credit.shared.reasonSame', {}] : ['credit.shared.reason', { customer, account }];
export const isWouldReset = (e) => !!e && e.code === WOULD_RESET && !!e.body && Array.isArray(e.body.affected);
// The credit keys of a partner write when the customer is on a shared account: none (cash only is the only state), unless the admin
// explicitly cleared a legacy limit -> cash-only keys.
export const sharedCreditKeys = (clear) => (clear ? { credit_limit: '', credit_unlimited: false } : {});

// Partners row / endpoint -> 'unlimited' | 'cash_only' | 'limited' (ruling 1: null and 0 are cash only).
export function creditStateOf(p) {
  if (!p) return 'cash_only';
  if (p.credit_unlimited === true || p.state === 'unlimited') return 'unlimited';
  return isNonZeroText(p.credit_limit) ? 'limited' : 'cash_only';
}

// GET /credit-exposure -> the indicator model: tone + which label, all figures passed through as text.
export function exposureIndicator(x) {
  if (!x) return null;
  const state = x.state || creditStateOf(x);
  const over = x.over_limit === true;
  const tone = over ? 'crit' : state === 'unlimited' ? 'info' : state === 'cash_only' ? 'mut' : 'good';
  const label = over ? 'over' : state === 'limited' ? 'within' : state;
  return { state, tone, label, exposure: x.exposure, limit: x.credit_limit, headroom: headroomOf(x.headroom), shared: !!x.shared_account };   // shared: the server's flag, no credit tracked per customer
}

// A batch row refused by the gate ({id,type,code,errors}) -> one line: the localised kind + the API's own text.
export function creditRowText(t, f) {
  const msg = ((f && f.errors) || []).join('; ');
  if (!f || !KIND[f.code]) return msg;
  const hint = f.code === 'CREDIT_LIMIT_WARNING' ? ' ' + t('credit.batch.postSingly') : '';
  return `${t(`credit.kind.${KIND[f.code]}`)} — ${msg}${hint}`;
}

// E5 (GES-440): GET /customers/:id/exposure-preview ({ state, limit, exposure, components, headroom, shared_account }) or the first customer of
// POST /customers/:id/exposure-preview ({ would_refuse, customers:[{ ...now, adds, exposure_after, headroom_after, would_refuse, refusal:{body} }] })
// -> one view. Everything is the server's text; nothing is computed here.
export function previewView(d) {
  const c = d && (Array.isArray(d.customers) ? d.customers[0] : d);
  if (!c) return null;
  return {
    state: c.state || creditStateOf(c), limit: c.limit, exposure: c.exposure, headroom: headroomOf(c.headroom),
    adds: c.adds != null ? c.adds : null, after: c.exposure_after != null ? c.exposure_after : null, headroomAfter: headroomOf(c.headroom_after),
    feesUnearned: c.components && c.components.fees_unearned != null ? c.components.fees_unearned : null,
    duesElsewhere: c.dues_elsewhere === true, duesAmount: c.dues_elsewhere_amount != null ? c.dues_elsewhere_amount : null,   // E6 (R-I): the server's flag + 2-dp text
    shared: c.shared_account || null, wouldRefuse: c.would_refuse === true, refusal: (c.refusal && c.refusal.body) || null,
  };
}
// A 404 (or any non-2xx) hides the strip: the API may not serve the preview yet.
export const previewServed = (r) => !!r && r.ok === true && !!r.data;
